// ===================================================================
// src/app/components/layout/SupportChatLauncher.tsx
//
// Botão flutuante (canto inferior esquerdo da área de conteúdo) que abre
// o canal de suporte do usuário com a equipe Eleve de qualquer tela, sem
// sair do que está fazendo. NÃO é um canal novo: é o mesmo ChatGroup
// kind='support' que já aparece no Chat da Equipe (um por usuário,
// provisionado no backend por apps/chat/signals.py::
// create_support_chat_group_for_profile) — histórico fica num lugar só,
// e a equipe continua respondendo pelo /interno/chat.
//
// Conversa ≠ chamado: dúvida rápida vai aqui; bug/melhoria que precisa de
// protocolo e acompanhamento vai pra /suporte (link discreto no rodapé).
//
// Estado aberto/fechado + id do canal ficam em useSupportChat (store
// global) porque o AppLayout precisa deles no handler do WS de chat.
// ===================================================================

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { LifeBuoy, MessageCircleQuestion, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useChatNotifications, markGroupReadLocally } from '../../hooks/useChatNotifications';
import {
  useSupportChat,
  openSupportChat,
  closeSupportChat,
  setSupportChatGroupId,
} from '../../hooks/useSupportChat';
import { chatService, type ChatGroup } from '../../services/chatService';
import ChatThread from '../chat/ChatThread';

// Expectativa de resposta — chat passa sensação de resposta imediata;
// sem este aviso, silêncio fora do expediente vira frustração.
const RESPONSE_EXPECTATION = 'Respondemos em dias úteis, no horário comercial.';

export default function SupportChatLauncher() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { open } = useSupportChat();
  const { unreadByGroup } = useChatNotifications();
  const [group, setGroup] = useState<ChatGroup | null>(null);

  // Staff é quem atende o suporte — não tem canal kind='support' próprio.
  const eligible = !!user?.id && !user.is_staff;

  useEffect(() => {
    if (!eligible) return;
    let cancelled = false;
    chatService
      .listGroups()
      .then((groups) => {
        if (cancelled) return;
        const support = groups.find((g) => g.kind === 'support') ?? null;
        setGroup(support);
        setSupportChatGroupId(support?.id ?? null);
      })
      .catch(() => {
        // silencioso — sem canal carregado o botão simplesmente não aparece
      });
    return () => {
      cancelled = true;
    };
  }, [eligible, user?.id]);

  // Fecha ao trocar de usuário/deslogar, pra não reabrir na próxima sessão.
  useEffect(() => {
    if (!eligible) {
      closeSupportChat();
      setSupportChatGroupId(null);
    }
  }, [eligible]);

  const unread = group ? unreadByGroup[group.id] ?? 0 : 0;

  // Com o painel aberto, o ChatThread já marca como lido no backend a cada
  // mensagem — aqui só zera o badge local na mesma hora.
  useEffect(() => {
    if (open && group && unread > 0) markGroupReadLocally(group.id);
  }, [open, group, unread]);

  // No Chat da Equipe o canal de suporte já está na lista — o botão só
  // duplicaria a mesma conversa e cobriria a coluna de grupos.
  const onChatPage = location.pathname === '/chat' || location.pathname.startsWith('/chat/');

  useEffect(() => {
    if (onChatPage) closeSupportChat();
  }, [onChatPage]);

  if (!eligible || !group || onChatPage) return null;

  const handleReportProblem = () => {
    closeSupportChat();
    navigate('/suporte?novo=1');
  };

  return (
    <>
      {open && (
        <div
          role="dialog"
          aria-label="Suporte Eleve"
          className="absolute bottom-20 left-4 right-4 sm:right-auto sm:left-5 z-40 sm:w-[380px] h-[min(560px,calc(100%-6.5rem))] flex flex-col rounded-2xl overflow-hidden border border-slate-200 dark:border-white/[0.08] bg-white dark:bg-[#0a0b0d] shadow-2xl"
        >
          <div className="flex items-start justify-between gap-3 px-4 py-3 bg-indigo-600 text-white">
            <div className="min-w-0">
              <p className="text-sm font-semibold flex items-center gap-1.5">
                <LifeBuoy className="size-4" /> Suporte Eleve
              </p>
              <p className="text-[11px] text-indigo-100 mt-0.5">{RESPONSE_EXPECTATION}</p>
            </div>
            <button
              type="button"
              onClick={closeSupportChat}
              className="p-1 -mr-1 rounded-md text-indigo-100 hover:text-white hover:bg-white/10"
              aria-label="Fechar suporte"
            >
              <X className="size-4" />
            </button>
          </div>

          <div className="flex flex-1 min-h-0">
            <ChatThread
              key={group.id}
              group={group}
              currentUserId={user!.id}
              canDeleteAny={false}
              compact
              emptyHint="Ficou com alguma dúvida sobre a plataforma? Escreva aqui que a equipe Eleve te responde."
            />
          </div>

          <button
            type="button"
            onClick={handleReportProblem}
            className="px-4 py-2 text-[11px] text-left text-slate-500 dark:text-slate-400 border-t border-slate-200 dark:border-white/[0.08] hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-50 dark:hover:bg-white/[0.03]"
          >
            Encontrou um erro ou quer sugerir uma melhoria? <span className="underline">Abrir um chamado</span>
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => (open ? closeSupportChat() : openSupportChat())}
        className="group absolute bottom-5 left-4 sm:left-5 z-40 size-12 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg hover:shadow-xl flex items-center justify-center transition-all"
        aria-label={open ? 'Fechar suporte' : 'Falar com o suporte'}
        aria-expanded={open}
      >
        {open ? <X className="size-5" /> : <MessageCircleQuestion className="size-5" />}
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 border-2 border-white dark:border-[#0a0b0d] text-[10px] font-semibold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
        {!open && (
          <span className="absolute left-full ml-2 px-2.5 py-1 rounded-lg bg-slate-900 text-white text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Falar com o suporte
          </span>
        )}
      </button>
    </>
  );
}
